import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Modal Component - Accessible dialog with overlay, sizes, and focus trap
 * @ownyou/ui-components
 */
import { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@ownyou/ui-design-system';
const SIZE_CLASSES = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
};
export function Modal({ isOpen, onClose, title, description, size = 'md', children, footer, closeOnOverlay = true, }) {
    const closeButtonRef = useRef(null);
    const contentRef = useRef(null);
    const titleId = title ? 'modal-title' : undefined;
    // Focus close button on mount
    useEffect(() => {
        if (isOpen && closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
    }, [isOpen]);
    // Basic focus trap
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
            return;
        }
        if (e.key === 'Tab' && contentRef.current) {
            const focusableElements = contentRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements.length === 0)
                return;
            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }, [onClose]);
    const handleOverlayClick = useCallback((e) => {
        if (closeOnOverlay && e.target === e.currentTarget) {
            onClose();
        }
    }, [closeOnOverlay, onClose]);
    if (!isOpen)
        return null;
    return createPortal(_jsx("div", { "data-testid": "modal-overlay", className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50", onClick: handleOverlayClick, onKeyDown: handleKeyDown, children: _jsxs("div", { ref: contentRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, className: cn('rounded-xl shadow-xl mx-4 overflow-hidden w-full', SIZE_CLASSES[size]), style: {
                background: 'var(--aurora-card-solid, white)',
                color: 'var(--aurora-ink, rgb(17 24 39))',
                borderRadius: 'var(--aurora-radius, 0.75rem)',
                boxShadow: 'var(--aurora-card-shadow, 0 20px 25px -5px rgb(0 0 0 / 0.1))',
            }, onClick: (e) => e.stopPropagation(), children: [(title || true) && (_jsxs("div", { className: "px-6 pt-6 pb-2 flex items-start justify-between", children: [title && (_jsx("h2", { id: titleId, className: "text-heading font-semibold text-neutral-900", children: title })), _jsx("button", { ref: closeButtonRef, type: "button", onClick: onClose, "aria-label": "Close modal", className: "ml-auto opacity-60 hover:opacity-100 transition-opacity p-1 -mt-1 -mr-1", children: _jsxs("svg", { className: "w-5 h-5", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] })), description && (_jsx("p", { className: "px-6 text-caption text-neutral-500", children: description })), _jsx("div", { className: "px-6 py-4", children: children }), footer && (_jsx("div", { className: "px-6 pb-6 pt-2 flex justify-end gap-3", children: footer }))] }) }), document.body);
}
export default Modal;
